// src/components/common/Seo.jsx
//
// This app is a client-rendered SPA, so index.html only ships ONE static
// set of meta tags (the homepage's). This component lets any page override
// the title/description/canonical for itself once mounted - useful for
// facility pages, blog posts, etc. so each route describes itself properly
// to anyone sharing a link or to a JS-rendering crawler.
//
// Intentionally dependency-free (no react-helmet) - just a small effect
// that patches the existing <head> tags index.html already created.

import { useEffect } from "react";

const SITE_NAME = "Viroosh Sports Complex";
const DEFAULT_IMAGE = "https://viroosh.com/og-cover.jpg";
const SITE_URL = "https://viroosh.com";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * @param {string}  title        Full page title (SITE_NAME is appended automatically unless already included)
 * @param {string}  description  Meta description (~150-160 chars ideal)
 * @param {string}  [path]       Path for the canonical URL, e.g. "/facilities/badminton"
 * @param {string}  [image]      Absolute OG/Twitter image URL
 */
const Seo = ({ title, description, path = "", image = DEFAULT_IMAGE }) => {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", image);
    setMeta("property", "og:url", `${SITE_URL}${path}`);
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}${path}`);
  }, [title, description, path, image]);

  return null;
};

export default Seo;
